import { AlertCircle, ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type OnChangeFn,
  type RowData,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Aligns the column header and body cells. Defaults to `'left'`. */
    align?: 'left' | 'right' | 'center';
  }
}
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../feedback/EmptyState';
import { cn } from '@/shared/utils/cn';

export interface DataTableProps<TData> {
  columns: Array<ColumnDef<TData, unknown>>;
  data: TData[];
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  pageIndex?: number;
  pageSize?: number;
  pageCount?: number;
  totalRows?: number;
  onPageChange?: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  enableRowSelection?: boolean;
  selectedRows?: Record<string, boolean>;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  onRowClick?: (row: TData) => void;
  getRowId?: (row: TData, index: number) => string;
  tableId: string;
  caption?: string;
  className?: string;
  showColumnControls?: boolean;
  /** Row ids (from `getRowId` or TanStack row id) that should render `renderExpandedRow`. */
  expandedRowIds?: readonly string[];
  renderExpandedRow?: (row: TData) => ReactNode;
  getRowCanExpand?: (row: TData) => boolean;
}

type ColumnVisibilityState = Record<string, boolean>;

const COLUMN_VIS_KEY_PREFIX = 'byq.table.';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

function columnStorageKey(tableId: string) {
  return `${COLUMN_VIS_KEY_PREFIX}${tableId}.cols`;
}

function getInitialColumnVisibility(tableId: string): ColumnVisibilityState {
  try {
    const raw = window.localStorage.getItem(columnStorageKey(tableId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as ColumnVisibilityState;
  } catch {
    return {};
  }
}

function persistColumnVisibility(tableId: string, state: ColumnVisibilityState) {
  try {
    window.localStorage.setItem(columnStorageKey(tableId), JSON.stringify(state));
  } catch {
    // ignore storage errors (quota/private mode)
  }
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

type ColumnAlign = NonNullable<ColumnDef<unknown>['meta']>['align'];

function columnAlignClass(align: ColumnAlign | undefined): string {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}

function headerJustifyClass(align: ColumnAlign | undefined): string {
  if (align === 'right') return 'justify-end';
  if (align === 'center') return 'justify-center';
  return 'justify-start';
}

function buildPageItems(pageIndex: number, pageCount: number): Array<number | 'ellipsis'> {
  if (pageCount <= 0) return [];
  const current = clamp(pageIndex + 1, 1, pageCount);

  const pages = new Set<number>([1, pageCount]);
  for (let p = current - 2; p <= current + 2; p += 1) {
    if (p >= 1 && p <= pageCount) pages.add(p);
  }

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const items: Array<number | 'ellipsis'> = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const page = sorted[i];
    const prev = sorted[i - 1];
    if (prev && page - prev > 1) items.push('ellipsis');
    items.push(page);
  }
  return items;
}

export function DataTable<TData>({
  columns,
  data,
  isLoading,
  isError,
  errorMessage,
  sorting,
  onSortingChange,
  pageIndex,
  pageSize,
  pageCount,
  totalRows,
  onPageChange,
  onPageSizeChange,
  enableRowSelection,
  selectedRows,
  onRowSelectionChange,
  onRowClick,
  getRowId,
  tableId,
  caption,
  className,
  showColumnControls = true,
  expandedRowIds,
  renderExpandedRow,
  getRowCanExpand,
}: DataTableProps<TData>) {
  const { t } = useTranslation('common');
  const [isColsOpen, setIsColsOpen] = useState(false);
  /** Wraps the Columns control + dropdown so outside-click detection does not fire for in-menu clicks. */
  const columnMenuRef = useRef<HTMLDivElement | null>(null);

  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(() =>
    getInitialColumnVisibility(tableId),
  );

  useEffect(() => {
    persistColumnVisibility(tableId, columnVisibility);
  }, [columnVisibility, tableId]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      const menu = columnMenuRef.current;
      if (menu?.contains(target)) return;
      setIsColsOpen(false);
    };
    if (!isColsOpen) return undefined;
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isColsOpen]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(getRowId ? { getRowId } : {}),
    enableSorting: false,
    manualSorting: true,
    manualPagination: true,
    pageCount,
    enableRowSelection: enableRowSelection ?? false,
    state: {
      sorting: sorting ?? [],
      rowSelection: selectedRows ?? {},
      columnVisibility,
    },
    onSortingChange,
    onRowSelectionChange,
    onColumnVisibilityChange: setColumnVisibility,
  });

  const visibleLeafColumns = table.getVisibleLeafColumns();

  const pageItems = useMemo(
    () => (pageCount ? buildPageItems(pageIndex ?? 0, pageCount) : []),
    [pageCount, pageIndex],
  );

  const rangeText = useMemo(() => {
    if (totalRows === undefined || pageIndex === undefined || pageSize === undefined) return null;
    const start = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
    const end = Math.min(totalRows, (pageIndex + 1) * pageSize);
    return t('dataTable.range', { start, end, total: totalRows });
  }, [pageIndex, pageSize, totalRows, t]);

  const shouldShowPagination =
    typeof pageIndex === 'number' &&
    typeof pageSize === 'number' &&
    typeof pageCount === 'number' &&
    typeof onPageChange === 'function';

  const pageSizeOptions = useMemo(() => {
    const sizes = new Set<number>([...DEFAULT_PAGE_SIZE_OPTIONS]);
    if (typeof pageSize === 'number') sizes.add(pageSize);
    return Array.from(sizes).sort((a, b) => a - b);
  }, [pageSize]);

  const renderEmpty = !isLoading && !isError && data.length === 0;

  return (
    <div className={cn('w-full', className)}>
      {showColumnControls ? (
        <div className="mb-3 flex items-center justify-end">
          <div ref={columnMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setIsColsOpen((v) => !v)}
              className={cn(
                'inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg',
                'hover:bg-surface-muted',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
              )}
              aria-haspopup="menu"
              aria-expanded={isColsOpen}
            >
              <SlidersHorizontal aria-hidden="true" className="h-4 w-4 text-fg-muted" />
              <span>{t('dataTable.columns')}</span>
            </button>

            {isColsOpen ? (
              <div
                role="menu"
                className="absolute right-0 top-full z-10 mt-2 w-60 rounded-md border border-border bg-surface p-2 shadow-md"
              >
                <div className="max-h-72 overflow-auto">
                  {table
                    .getAllLeafColumns()
                    .filter((c) => c.getCanHide())
                    .map((col) => (
                      <label
                        key={col.id}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-fg hover:bg-surface-muted"
                      >
                        <input
                          type="checkbox"
                          checked={col.getIsVisible()}
                          onChange={col.getToggleVisibilityHandler()}
                          className="h-4 w-4 rounded border-border-strong accent-primary"
                        />
                        <span className="min-w-0 truncate">
                          {typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id}
                        </span>
                      </label>
                    ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="w-full overflow-hidden rounded-md border border-border bg-surface">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            {caption ? <caption className="sr-only">{caption}</caption> : null}
            <thead className="bg-surface-muted">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => {
                    const isSelectCol = header.column.id === 'select';
                    const align = header.column.columnDef.meta?.align;

                    return (
                      <th
                        key={header.id}
                        className={cn(
                          'align-middle whitespace-nowrap border-b border-border px-3 py-2 text-xs font-semibold text-fg-muted',
                          !isSelectCol && columnAlignClass(align),
                          isSelectCol && 'w-10 text-center',
                        )}
                        scope="col"
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={cn(
                              'flex w-full items-center',
                              isSelectCol ? 'justify-center' : headerJustifyClass(align),
                            )}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <tbody>
              {isError ? (
                <tr>
                  <td colSpan={Math.max(1, visibleLeafColumns.length)} className="px-4 py-10">
                    <div className="flex w-full flex-col items-center justify-center text-center">
                      <AlertCircle aria-hidden="true" className="h-10 w-10 text-danger" />
                      <div className="mt-3 text-sm font-medium text-fg">{t('state.error')}</div>
                      <div className="mt-1 text-sm text-fg-muted">
                        {errorMessage ?? t('dataTable.errorHint')}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : isLoading ? (
                Array.from({ length: 5 }).map((_, rowIdx) => (
                  <tr key={`sk-${rowIdx}`} className="border-b border-border">
                    {visibleLeafColumns.map((col, colIdx) => (
                      <td
                        key={`${col.id}-${colIdx}`}
                        className={cn(
                          'align-middle px-3 py-2 text-sm text-fg',
                          columnAlignClass(col.columnDef.meta?.align),
                        )}
                      >
                        <div
                          className={cn(
                            'h-4 animate-pulse rounded bg-border',
                            (rowIdx + colIdx) % 2 === 0 ? 'w-3/4' : 'w-1/2',
                          )}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              ) : renderEmpty ? (
                <tr>
                  <td colSpan={Math.max(1, visibleLeafColumns.length)} className="px-4 py-10">
                    <EmptyState
                      icon={AlertCircle}
                      title={t('dataTable.emptyTitle')}
                      description={t('dataTable.emptyDescription')}
                    />
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => {
                  const isSelected = row.getIsSelected();
                  const isClickable = Boolean(onRowClick);
                  const canExpand = getRowCanExpand
                    ? getRowCanExpand(row.original)
                    : Boolean(renderExpandedRow);
                  const isExpanded =
                    canExpand && expandedRowIds ? expandedRowIds.includes(row.id) : false;

                  return (
                    <Fragment key={row.id}>
                      <tr
                        tabIndex={0}
                        onClick={() => {
                          if (!onRowClick) return;
                          onRowClick(row.original);
                        }}
                        onKeyDown={(e) => {
                          if (!onRowClick) return;
                          if (e.key === 'Enter') onRowClick(row.original);
                        }}
                        className={cn(
                          'border-b border-border transition-colors duration-150',
                          'hover:bg-surface-muted',
                          isSelected && 'bg-primary-subtle',
                          isClickable && 'cursor-pointer',
                          isExpanded && 'bg-surface-muted',
                          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary',
                        )}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const isSelectCol = cell.column.id === 'select';
                          const align = cell.column.columnDef.meta?.align;
                          return (
                            <td
                              key={cell.id}
                              className={cn(
                                'align-middle px-3 py-2 text-sm text-fg',
                                isSelectCol ? 'w-10 text-center' : columnAlignClass(align),
                              )}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                      </tr>
                      {isExpanded && renderExpandedRow ? (
                        <tr className="border-b border-border">
                          <td colSpan={Math.max(1, visibleLeafColumns.length)} className="p-0">
                            {renderExpandedRow(row.original)}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {shouldShowPagination ? (
          <div className="flex flex-col gap-2 border-t border-border bg-surface px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-fg-muted">{rangeText ?? null}</div>

            <div className="flex items-center justify-center gap-1">
              <button
                type="button"
                onClick={() => onPageChange?.((pageIndex ?? 0) - 1)}
                disabled={(pageIndex ?? 0) <= 0}
                className={cn(
                  'inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-fg-muted hover:bg-surface-muted',
                  (pageIndex ?? 0) <= 0 && 'cursor-not-allowed opacity-50 hover:bg-transparent',
                )}
              >
                <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                <span>{t('dataTable.prev')}</span>
              </button>

              {pageItems.map((item, idx) =>
                item === 'ellipsis' ? (
                  <span key={`e-${idx}`} className="px-2 text-sm text-fg-muted">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    onClick={() => onPageChange?.(item - 1)}
                    className={cn(
                      'rounded px-2 py-1 text-sm',
                      (pageIndex ?? 0) === item - 1
                        ? 'bg-primary text-fg-on-primary'
                        : 'text-fg-muted hover:bg-surface-muted',
                    )}
                  >
                    {item}
                  </button>
                ),
              )}

              <button
                type="button"
                onClick={() => onPageChange?.((pageIndex ?? 0) + 1)}
                disabled={typeof pageCount === 'number' ? (pageIndex ?? 0) >= pageCount - 1 : false}
                className={cn(
                  'inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-fg-muted hover:bg-surface-muted',
                  typeof pageCount === 'number' && (pageIndex ?? 0) >= pageCount - 1
                    ? 'cursor-not-allowed opacity-50 hover:bg-transparent'
                    : null,
                )}
              >
                <span>{t('dataTable.next')}</span>
                <ChevronRight aria-hidden="true" className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
              <span className="text-center text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-muted sm:text-left">
                {t('dataTable.rowsPerPage')}
              </span>
              <div
                role="radiogroup"
                aria-label={t('dataTable.rowsPerPage')}
                className="inline-flex self-center rounded-lg border border-border bg-surface-muted p-0.5 shadow-inner sm:self-auto"
              >
                {pageSizeOptions.map((size) => {
                  const selected = pageSize === size;
                  return (
                    <button
                      key={size}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => onPageSizeChange?.(size)}
                      className={cn(
                        'min-w-[2.75rem] rounded-md px-3 py-1.5 text-sm font-semibold tabular-nums transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                        selected
                          ? 'bg-surface text-fg shadow-sm ring-1 ring-border/80'
                          : 'text-fg-muted hover:bg-surface/90 hover:text-fg',
                      )}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* --- USAGE EXAMPLE ---
        In features/product-master/components/BrandsTable.tsx:

        const BrandsTable = () => {
          const navigate = useNavigate()
          const { sorting, setSorting, pageIndex, pageSize, setPageIndex }
            = useTableState({ tableId: 'brands', defaultPageSize: 25 })

          const { data, isLoading, isError } = useBrandsList({
            sortBy: sorting[0]?.id,
            sortDir: sorting[0]?.desc ? 'desc' : 'asc',
            page: pageIndex,
            pageSize
          })

          const columns: ColumnDef<Brand>[] = [
            { id: 'select', header: ..., cell: ... }, // checkbox col
            { accessorKey: 'name', header: 'Brand Name', enableSorting: true },
            { accessorKey: 'productCount', header: 'Products' },
            { accessorKey: 'status', header: 'Status',
              cell: ({ row }) => <StatusPill status={row.original.status} /> },
          ]

          return (
            <DataTable
              tableId="brands"
              columns={columns}
              data={data?.items ?? []}
              isLoading={isLoading}
              isError={isError}
              pageCount={data?.pageCount}
              totalRows={data?.total}
              pageIndex={pageIndex}
              pageSize={pageSize}
              sorting={sorting}
              onSortingChange={setSorting}
              onPageChange={setPageIndex}
              onRowClick={(row) => navigate(`/product-master/brand/${row.id}`)}
            />
          )
        }
      */}
    </div>
  );
}
